'use client'
import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import styled, { css } from 'styled-components';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useLanguage } from '@/contexts/LanguageContext';
import DashboardSidebar from '@/components/Layouts/Sidebar/DashboardSidebar.jsx';
import LoadingOverlay from '@/components/UI/LoadingOverlay.jsx';

import { AnimatedBackground } from '@/components/Containers/StyledCotainers.js';
import { ModuleViewProvider } from '@/contexts/ModuleViewContext';
import MainSideBar from '@/components/Layouts/MainSideBar.jsx';
import UpdateBanner from '@/components/UI/UpdateBanner.jsx';

// --- Static Imports ---
import MouseModule from '@/components/Modules/MouseModule.jsx';
import CrosshairModule from '@/components/Modules/CrosshairModule.jsx';
import ViewmodelModule from '@/components/Modules/ViewmodelModule.jsx';
import HudModule from '@/components/Modules/HudModule.jsx';
import BindsBasicModule from '@/components/Modules/BindsBasicModule.jsx';
import BindsSpecialModule from '@/components/Modules/BindsSpecialModule.jsx';
import BindsCustomModule from '@/components/Modules/BindsCustomModule.jsx';
import LaunchOptionsPage from '@/components/Pages/LaunchOptionsPage.jsx';
import GraphicsModule from '@/components/Modules/GraphicsModule.jsx';
import PracticeModule from '@/components/Modules/PracticeModule.jsx';
import RadioModule from '@/components/Modules/RadioModule.jsx';

// --- Static Pages ---
import ManagerView from '@/components/Pages/ManagerView.jsx';
import WikiView from '@/components/Pages/WikiView.jsx';
import UpdatesView from '@/components/Pages/UpdatesView.jsx';
import BlogView from '@/components/Pages/BlogView.jsx';
import ExportPage from '@/components/Pages/ExportPage.jsx'
import SettingsView from '@/components/Pages/SettingsView.jsx';
import LoginCard from '@/components/Auth/LoginCard.jsx';
import AccountView from '@/components/Auth/AccountView.jsx';
import Landing from '@/components/Landing/Landing.jsx';
import { COMING_SOON_MODULES } from '@/utils/constants';
import { usePro } from '@/contexts/ProContext';




const DashboardContainer = styled.div`
    display: flex;
    width: 100vw;
    height: 100vh;
    padding: 0;
    margin: 0;
    overflow: hidden;
    position: relative;
    min-width: 1288px;
`;

const SidebarColumn = styled.div`
    flex-shrink: 0;
    width: 280px;
    height: 100%;
    z-index: 20;
    background: rgba(13, 17, 23, 0.3);
    backdrop-filter: blur(5px);
`;

const CenterColumn = styled.div`
    flex: 1;
    position: relative;
    scroll-behavior: smooth;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(99, 102, 241, 0.2);
        border-radius: 4px;
        transition: background 0.2s;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(99, 102, 241, 0.4);
    }
`;

const ContentScrollArea = styled.div`
    padding: 20px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`;


const FadeInDiv = styled.div`
    animation: fadeIn 0.4s ease-out;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-shrink: 0; 
    padding-right: 180px;
    padding-left: 10px;
    height: calc(100% - 56px);

    
    flex-grow: ${({ $fullHeight }) => ($fullHeight ? 1 : 0)}; 
    height: ${({ $fullHeight }) => ($fullHeight ? '100%' : 'auto')};
    
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 1600px) {
        padding-right: 0;
    }

    /* El padding-right de arriba existe para dejarle aire a los previews del
       editor. En vistas que se centran solas (login/cuenta) descentra la
       tarjeta casi 90px en pantallas anchas. */
    ${({ $centered }) => $centered && css`
        padding-right: 0;
        padding-left: 0;
        align-items: center;

        @media (max-width: 1600px) {
            padding-right: 0;
        }
    `}
`;

const StandardWrapper = styled.div`
    width: 100%;
    max-width: 1000px;
    min-height: 100%;
`;

const SectionTitle = styled.h1`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 1.7rem;
    font-weight: 800;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 3px;
    margin: 10px 0 20px 20px;
    padding-bottom: 10px;
    //border-bottom: 1px solid rgba(189, 181, 255, 0.3);
    width: 100%;
    max-width: 1000px;
    animation: fadeIn 0.3s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-5px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

const PendingOverlayContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    background: rgba(8, 10, 15, 0.55); /* Semi-transparent dark background */
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: all; /* Block clicks while loading */
    animation: fadeIn 0.4s ease-out;
`;

const DashboardPage = () => {
    const { t } = useLanguage();
    const { configName, resetConfig, userConfigs, focusSignal, clearFocusSignal } = useConfig();
    const { hasFeature } = usePro();
    const { isAuthenticated, loading: authLoading } = useAuth();

    const router = useRouter();
    const pathname = usePathname();

    // El ?error=terms de /api/auth/steam se lee del location y no con
    // useSearchParams(): este árbol se prerenderiza (generateStaticParams) y
    // useSearchParams() sin <Suspense> rompe el build.
    const [loginError, setLoginError] = useState(null);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        setLoginError(new URLSearchParams(window.location.search).get('error'));
    }, [pathname]);

    // Helper to parse path
    const getInitialState = (path) => {
        // La raíz es la home pública (sección 'home'), no el editor.
        if (!path) return { main: 'home', sub: 'home' };

        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        if (!cleanPath) return { main: 'home', sub: 'home' };

        const parts = cleanPath.split('/');
        const main = parts[0];
        const sub = parts[1];

        if (main === 'editor') {
            return { main: 'editor', sub: sub || 'crosshair' };
        // /login (público, al que rebota el middleware sin sesión) y /account
        // (gateado) son la misma pestaña: el content decide qué mostrar según
        // haya sesión o no.
        } else if (main === 'login' || main === 'account') {
            return { main: 'account', sub: 'account' };
        } else if (['manager', 'wiki', 'blog', 'updates', 'exportpage', 'settings'].includes(main)) {
            return { main: main, sub: main };
        } else {
            return { main: 'editor', sub: 'crosshair' };
        }
    };

    // Initialize state lazily from pathname
    const [state, setState] = useState(() => getInitialState(pathname));
    const { main: mainTab, sub: activeTab } = state;
    const [hasAttemptedAutoLoad, setHasAttemptedAutoLoad] = useState(false);

    const setMainTab = (val) => setState(prev => ({ ...prev, main: val }));
    const setActiveTab = (val) => setState(prev => ({ ...prev, sub: val }));

    // Sync URL to State (only if URL changes and mismatches current state)
    useEffect(() => {
        const newState = getInitialState(pathname);
        if (newState.main !== mainTab || newState.sub !== activeTab) {
            setState(newState);
        }
    }, [pathname]);

    const [isPending, startTransition] = React.useTransition();
    const [targetRoute, setTargetRoute] = useState(null);
    const visitedRoutes = React.useRef(new Set());

    // Se registra la ruta actual como visitada una vez que renderiza
    useEffect(() => {
        const currentRoute = mainTab === 'editor' ? `editor-${activeTab}` : mainTab;
        visitedRoutes.current.add(currentRoute);
        // Limpiamos la ruta objetivo al completar la transición
        if (!isPending) {
            setTargetRoute(null);
        }
    }, [mainTab, activeTab, isPending]);

    const handleMainTabChange = (tabId) => {
        setTargetRoute(tabId);
        startTransition(() => {
            if (tabId === 'editor') {
                router.push('/editor/crosshair');
            } else if (tabId === 'home') {
                router.push('/');
            } else if (tabId === 'account') {
                // Sin sesión la URL tiene que ser /login: /account está detrás
                // del gate del middleware y rebotaría igual, con un redirect
                // de más en el medio.
                router.push(isAuthenticated ? '/account' : '/login');
            } else {
                router.push(`/${tabId}`);
            }
        });
    };

    const handleActiveTabChange = (tabId) => {
        setActiveTab(tabId);
        if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', `/editor/${tabId}/`);
        }
    };

    const shouldShowLoading = isPending && targetRoute && !visitedRoutes.current.has(targetRoute);



    useEffect(() => {
        if (focusSignal && focusSignal.moduleKey) {
            // Translate focus signal to URL navigation
            const editorModules = [
                'crosshair', 'mouse', 'viewmodel', 'hud', 'binds',
                'binds-basic', 'binds-special', 'binds-custom',
                'video', 'practice', 'radio', 'launch'
            ];

            if (editorModules.includes(focusSignal.moduleKey)) {
                handleMainTabChange('editor');
                handleActiveTabChange(focusSignal.moduleKey);
            } else {
                router.push(`/${focusSignal.moduleKey}`);
            }
            clearFocusSignal()
        }
    }, [focusSignal, router, clearFocusSignal]);

    useEffect(() => {
        if (!hasAttemptedAutoLoad && userConfigs && userConfigs.length > 0) {
            if (!configName) {
                const defaultConfig = userConfigs.find(c => c.name === 'Default Config' || c.name === 'Default') || userConfigs[0];
                if (defaultConfig) {
                    resetConfig(defaultConfig.name);
                }
            }
            setHasAttemptedAutoLoad(true);
        } else if (!hasAttemptedAutoLoad && userConfigs && userConfigs.length === 0) {
            // Si después de fetchUserConfigs sigue en 0, marcamos como intentado pero no creamos nada extra.
            setHasAttemptedAutoLoad(true);
        }
    }, [userConfigs, configName, hasAttemptedAutoLoad, resetConfig]);


    const getTabLabel = (tab) => {
        const labels = {
            crosshair: 'Crosshair',
            mouse: 'Mouse / Sens',
            viewmodel: 'Viewmodel',
            hud: 'HUD / Radar',
            'binds-basic': 'Basic Binds',
            'binds-special': 'Special Binds',
            'binds-custom': t('BINDS_CUSTOM_TITLE'),
            radio: t('RADIO_COMMUNICATION_TITLE'),
            practice: 'Practice',
            video: 'Video / FPS',
            launch: 'Launch Options',
        };
        return labels[tab] || tab;
    };

    const blockIfInactive = (moduleKey, content) => {
        if (!moduleKey || hasFeature('modulos_avanzados') || !COMING_SOON_MODULES.includes(moduleKey)) return content;
        return (
            <div style={{ position: 'relative', width: '100%' }}>
                <div style={{ pointerEvents: 'none' }}>{content}</div>
                <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'not-allowed' }} />
            </div>
        );
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'crosshair':
                return (
                    <FadeInDiv>
                        <StandardWrapper>
                            <CrosshairModule id="module-crosshair" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'viewmodel':
                return (
                    <FadeInDiv>
                        <StandardWrapper>
                            <ViewmodelModule id="module-viewmodel" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'hud':
                return (
                    <FadeInDiv>
                        <StandardWrapper>
                            <HudModule id="module-hud" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'binds-basic':
                return (
                    <FadeInDiv>
                        <StandardWrapper>
                            <BindsBasicModule id="module-binds-basic" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'binds-special':
                return blockIfInactive('binds_special', (
                    <FadeInDiv>
                        <StandardWrapper>
                            <BindsSpecialModule id="module-binds-special" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                ));
            case 'binds-custom':
                return blockIfInactive('binds_custom', (
                    <FadeInDiv>
                        <StandardWrapper style={{ maxWidth: '1200px' }}>
                            <BindsCustomModule id="module-binds-custom" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                ));
            case 'mouse':
                return (
                    <FadeInDiv>
                        <StandardWrapper>
                            <MouseModule id="module-mouse" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'video':
                return blockIfInactive('graphics', (
                    <FadeInDiv>
                        <StandardWrapper>
                            <GraphicsModule id="module-graphics" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                ));
            case 'practice':
                return blockIfInactive('practice', (
                    <FadeInDiv>
                        <StandardWrapper>
                            <PracticeModule id="module-practice" initialExpanded={true} />
                        </StandardWrapper>
                    </FadeInDiv>
                ));
            case 'radio':
                return blockIfInactive('radio', (
                    <FadeInDiv>
                        <StandardWrapper>
                            <RadioModule id="module-radio" />
                        </StandardWrapper>
                    </FadeInDiv>
                ));
            case 'launch':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper>
                            <LaunchOptionsPage />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'manager':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper>
                            <ManagerView />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'wiki':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper>
                            <WikiView />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'blog':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper style={{ maxWidth: '1200px' }}>
                            <BlogView />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'updates':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper style={{ maxWidth: '1000px' }}>
                            <UpdatesView />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'exportpage':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper>
                            <ExportPage />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'settings':
                return (
                    <FadeInDiv $fullHeight={true}>
                        <StandardWrapper>
                            <SettingsView />
                        </StandardWrapper>
                    </FadeInDiv>
                );
            case 'account':
                // Mientras AuthContext resuelve /api/auth/me no dibujamos nada:
                // mostrar el login y reemplazarlo medio segundo después por la
                // cuenta (o al revés) es un parpadeo feo y confuso.
                if (authLoading) return null;
                return (
                    <FadeInDiv $fullHeight={true} $centered={true}>
                        <StandardWrapper>
                            {isAuthenticated
                                ? <AccountView />
                                : <LoginCard error={loginError} />}
                        </StandardWrapper>
                    </FadeInDiv>
                );
            default:
                return null;
        }
    };

    return (
        <DashboardContainer>
            {shouldShowLoading && (
                <PendingOverlayContainer>
                    <LoadingOverlay text={t('LOADING') || "LOADING..."} />
                </PendingOverlayContainer>
            )}
            <AnimatedBackground />

            {/* Columna 0: Mini Sidebar Principal */}
            <MainSideBar
                activeTab={mainTab}
                onTabChange={handleMainTabChange}
            />


            {/* Columna 1: Sidebar de Módulos (Solo se muestra en el Editor) */}
            {mainTab === 'editor' && (
                <SidebarColumn>
                    <DashboardSidebar
                        activeTab={activeTab}
                        setActiveTab={handleActiveTabChange}
                    />
                </SidebarColumn>
            )}

            {/* Columna 2: Center Content.
                La home va a sangre, fuera del ContentScrollArea: ese wrapper
                mete padding y centra con un max-width pensado para los módulos
                del editor, y la home trae sus propios anchos y degradés a lo
                largo de toda la columna. */}
            <CenterColumn>
                {mainTab === 'home' ? (
                    <Landing />
                ) : (
                    <ContentScrollArea>
                        {mainTab === 'editor' && activeTab && (
                            <SectionTitle key={activeTab}>{getTabLabel(activeTab)}</SectionTitle>
                        )}
                        <ModuleViewProvider isDashboard={true} navigateToManager={() => handleMainTabChange('manager')}>
                            {renderContent()}
                        </ModuleViewProvider>
                    </ContentScrollArea>
                )}
            </CenterColumn>



            <UpdateBanner />
        </DashboardContainer>
    );
};

export default DashboardPage;
